// PATH: src/app/contexts/auth/Provider.jsx
import { useCallback, useEffect, useReducer, useRef } from "react";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import { toast } from "sonner";

import { normalDecryptData, normalEncryptData } from "configs/encryption";
import { isTokenValid, setSession } from "utils/jwt";
import { disableGoogleAutoSelect } from "utils/googleIdentity";
import {
  AUTH_EXPIRED_EVENT,
  clearAllClientData,
  clearSessionCookies,
  getSessionStatus,
  hasAnySessionCookie,
  hasValidSession,
  isFilled,
  markManualLogout,
  writeSessionCookies,
} from "utils/session";
import { AuthContext } from "./context";
import { getGoogleLogin, getGoogleRegister } from "api/login/login";

// ----------------------------------------------------------------------

const DEFAULT_ROLE = [100000];
const SESSION_CHECK_INTERVAL = 30 * 1000; // re-validate every 30s

const initialState = {
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  errorMessage: null,
  user: null,
  role: [],
  roleid: null,
  rolename: null,
  profileImg: null,
};

const loggedOutPayload = {
  isAuthenticated: false,
  user: null,
  role: [],
  roleid: null,
  rolename: null,
  profileImg: null,
};

// ----------------------------------------------------------------------

const reducerHandlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, role, roleid, rolename, profileImg } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      role,
      roleid,
      rolename,
      profileImg,
    };
  },

  LOGIN_REQUEST: (state) => ({
    ...state,
    isLoading: true,
  }),

  LOGIN_SUCCESS: (state, action) => {
    const { user, role, roleid, rolename, profileImg } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      isInitialized: true,
      errorMessage: null,
      user,
      role,
      roleid,
      rolename,
      profileImg,
    };
  },

  LOGIN_ERROR: (state, action) => ({
    ...state,
    errorMessage: action.payload.errorMessage,
    isLoading: false,
  }),

  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isLoading: false,
    user: null,
    role: [],
    roleid: null,
    rolename: null,
    profileImg: null,
  }),
};

const reducer = (state, action) => {
  const handler = reducerHandlers[action.type];
  return handler ? handler(state, action) : state;
};

// ----------------------------------------------------------------------
// Role cookie parsing is isolated: a broken / old-format role cookie must
// NEVER log the user out (before, it threw inside init() and killed the
// whole session, leaving access_token + email behind -> half-logged-in state).
// Also handles the old double-JSON.stringify format.
const parseRoleCookie = () => {
  try {
    const raw = Cookies.get("role");
    if (!raw) return DEFAULT_ROLE;

    let parsed = JSON.parse(normalDecryptData(raw));
    if (typeof parsed === "string") parsed = JSON.parse(parsed);

    return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_ROLE;
  } catch {
    return DEFAULT_ROLE;
  }
};

const readAuthPayloadFromCookies = () => {
  const name = Cookies.get("name") || "";
  const email = Cookies.get("email");
  const id = Cookies.get("userid");
  const profileImg = isFilled(Cookies.get("profileImg")) ? Cookies.get("profileImg") : null;

  const roleidParsed = parseInt(Cookies.get("roleid"), 10);
  const roleid = Number.isNaN(roleidParsed) ? null : roleidParsed;
  const rolename = Cookies.get("rolename") || null;

  return {
    isAuthenticated: true,
    user: { name, email, id, profileImg },
    role: parseRoleCookie(),
    roleid,
    rolename,
    profileImg,
  };
};

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Re-checks cookies + JWT. Logs out if the session is dead, re-syncs user
  // if another tab logged in as someone else. Returns true if session valid.
  const verifySession = useCallback((notify = false) => {
    const status = getSessionStatus();
    const current = stateRef.current;

    if (status.valid) {
      setSession(status.token); // keep axios header + localStorage in sync

      const cookieUserId = String(Cookies.get("userid"));
      if (!current.isAuthenticated || String(current.user?.id) !== cookieUserId) {
        dispatch({ type: "LOGIN_SUCCESS", payload: readAuthPayloadFromCookies() });
      }
      return true;
    }

    // dead / partial session -> wipe every leftover cookie
    if (hasAnySessionCookie()) clearSessionCookies();
    setSession(null);

    if (current.isAuthenticated) {
      dispatch({ type: "LOGOUT" });
      if (notify) toast.error("Your session has ended. Please log in again.");
    }
    return false;
  }, []);

  // ✅ INIT
  useEffect(() => {
    const status = getSessionStatus();

    if (status.valid) {
      setSession(status.token);
      dispatch({ type: "INITIALIZE", payload: readAuthPayloadFromCookies() });
    } else {
      // e.g. access_token survived but email/userid vanished, or token expired
      if (hasAnySessionCookie()) clearSessionCookies();
      setSession(null);
      dispatch({ type: "INITIALIZE", payload: loggedOutPayload });
    }
  }, []);

  // ✅ KEEP SESSION HONEST WHILE THE APP IS OPEN
  // - every 30s (token can expire while the tab is open)
  // - when tab regains focus / becomes visible
  // - when another tab logs in/out (localStorage "authToken" changes)
  // - when axios sees a 401 (AUTH_EXPIRED_EVENT)
  useEffect(() => {
    if (!state.isInitialized) return undefined;

    const check = () => verifySession(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const onStorage = (e) => {
      if (e.key === null || e.key === "authToken") check();
    };

    const timer = setInterval(check, SESSION_CHECK_INTERVAL);
    window.addEventListener("focus", check);
    window.addEventListener("storage", onStorage);
    window.addEventListener(AUTH_EXPIRED_EVENT, check);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", check);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(AUTH_EXPIRED_EVENT, check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [state.isInitialized, verifySession]);

  // ✅ GOOGLE LOGIN (only login method now)
  const googleLogin = async (payload) => {
    dispatch({ type: "LOGIN_REQUEST" });

    try {
      const data = await getGoogleLogin(payload);

      if (data?.code === 200) {
        const { access_token, student } = data.data || {};

        const name = `${student?.FirstName || ""} ${student?.LastName || ""}`.trim();
        const email = student?.Email;
        const userid = student?.ID;
        const profileImg = student?.ProfileImg || null;
        const roleAccess = DEFAULT_ROLE;
        const roleId = null;
        const rolename = null;

        // Never trust a half-baked response: token must be a real, unexpired
        // JWT and email + userid must exist. Otherwise nothing is saved.
        const responseOk =
          isTokenValid(access_token) &&
          isFilled(String(email ?? "")) &&
          isFilled(String(userid ?? ""));

        if (!responseOk) {
          clearSessionCookies();
          setSession(null);
          dispatch({
            type: "LOGIN_ERROR",
            payload: { errorMessage: "Invalid login response" },
          });
          return { code: 500, message: "Login failed: invalid response from server. Please try again." };
        }

        // wipe leftovers of any previous session, then write fresh cookies
        clearSessionCookies();
        writeSessionCookies({
          token: access_token,
          name,
          email,
          userid,
          profileImg,
          // normalEncryptData already JSON.stringify()s -> don't stringify twice
          role: normalEncryptData(roleAccess),
          roleid: roleId,
          rolename,
        });

        // browser may block cookies - confirm they were really saved
        if (!hasValidSession()) {
          clearSessionCookies();
          setSession(null);
          dispatch({
            type: "LOGIN_ERROR",
            payload: { errorMessage: "Cookies blocked" },
          });
          return { code: 500, message: "Couldn't save your session. Please enable cookies and try again." };
        }

        setSession(access_token);

        dispatch({
          type: "LOGIN_SUCCESS",
          payload: {
            user: { name, email, id: userid, profileImg },
            role: roleAccess,
            roleid: roleId,
            rolename,
            profileImg,
          },
        });

        return data;
      }

      dispatch({
        type: "LOGIN_ERROR",
        payload: { errorMessage: data?.message },
      });

      return data;
    } catch (err) {
      dispatch({
        type: "LOGIN_ERROR",
        payload: { errorMessage: "Google Login Failed" },
      });
      return { code: 500, message: "Network Error" };
    }
  };

  // ✅ GOOGLE REGISTER (background — no session side-effects; caller
  // follows up with googleLogin() to actually sign the new user in)
  const googleRegister = async (payload) => {
    try {
      const data = await getGoogleRegister(payload);
      return data;
    } catch (err) {
      console.error("Google Register Error:", err);
      return { code: 500, message: "Network Error" };
    }
  };

  // ✅ LOGOUT
  // Works the same whether the user is "Aspirant"/guest-looking or fully
  // logged in: wipes ALL cookies + localStorage + sessionStorage + axios
  // header, tells Google not to auto-sign this user back in, then
  // hard-redirects to the home page. The login modal is NOT opened afterwards.
  const logout = () => {
    markManualLogout(); // AuthGuard must not open the login modal now

    clearAllClientData();
    setSession(null);
    disableGoogleAutoSelect(); // otherwise One Tap would log the same user straight back in
    dispatch({ type: "LOGOUT" });

    // Hard redirect = no stale in-memory state, lands on home page, no modal
    window.location.replace("/");
  };

  if (!state.isInitialized) return null;

  return (
    <AuthContext
      value={{
        ...state,
        logout,
        googleLogin,
        googleRegister,
        verifySession,
      }}
    >
      {children}
    </AuthContext>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};